import { useState, useEffect } from 'react';
import { leakhunterAPI } from '../services/api';
import { useEnvironment } from '../context/EnvironmentContext';
import JsonViewer from '../components/JsonViewer';
import PoolStatsPanel from '../components/admin/pool/PoolStatsPanel';
import {
  BugAntIcon,
  PlayIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ServerStackIcon,
  ChartBarIcon,
  ListBulletIcon,
  Cog6ToothIcon,
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
} from '@heroicons/react/24/outline';

// Status badge component
function StatusBadge({ status }) {
  const styles = {
    healthy: 'bg-green-100 text-green-800 border-green-300',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    critical: 'bg-red-100 text-red-800 border-red-300',
  };

  const icons = {
    healthy: <CheckCircleIcon className="w-4 h-4" />,
    warning: <ExclamationTriangleIcon className="w-4 h-4" />,
    critical: <XCircleIcon className="w-4 h-4" />,
  };

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium border ${styles[status] || styles.healthy}`}>
      {icons[status]}
      {status?.charAt(0).toUpperCase() + status?.slice(1)}
    </span>
  );
}

// Metric card component
function MetricCard({ label, value, subValue, icon: Icon, color = 'blue' }) {
  const colorStyles = {
    blue: 'bg-blue-50 border-blue-200 text-blue-600',
    green: 'bg-green-50 border-green-200 text-green-600',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-600',
    red: 'bg-red-50 border-red-200 text-red-600',
    purple: 'bg-purple-50 border-purple-200 text-purple-600',
  };

  return (
    <div className={`card border ${colorStyles[color] || colorStyles.blue}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{label}</p>
          <p className="text-2xl font-bold mt-1">{value ?? 'N/A'}</p>
          {subValue && <p className="text-xs text-gray-500 mt-1">{subValue}</p>}
        </div>
        {Icon && <Icon className="w-8 h-8 opacity-50" />}
      </div>
    </div>
  );
}

// Tabs component
function Tabs({ tabs, activeTab, onTabChange }) {
  return (
    <div className="border-b border-gray-200 mb-6">
      <nav className="flex -mb-px space-x-8">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => onTabChange(tab.id)}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <span className="flex items-center gap-2">
              {tab.icon && <tab.icon className="w-5 h-5" />}
              {tab.label}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
}

// Sortable column header component
function SortableHeader({ label, column, sortState, onSort, align = 'left' }) {
  const isActive = sortState.column === column;
  const direction = isActive ? sortState.direction : null;

  const handleClick = () => {
    if (isActive) {
      // Toggle direction
      onSort({ column, direction: direction === 'asc' ? 'desc' : 'asc' });
    } else {
      // Default to descending for new column
      onSort({ column, direction: 'desc' });
    }
  };

  return (
    <th
      className={`py-2 px-3 cursor-pointer hover:bg-gray-50 select-none ${
        align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'
      }`}
      onClick={handleClick}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {direction === 'asc' ? (
          <ChevronUpIcon className="w-4 h-4 text-blue-600" />
        ) : direction === 'desc' ? (
          <ChevronDownIcon className="w-4 h-4 text-blue-600" />
        ) : (
          <ChevronUpDownIcon className="w-4 h-4 text-gray-400" />
        )}
      </span>
    </th>
  );
}

function resolveFetchMeta(analysis) {
  if (!analysis) return null;
  return analysis.fetch_metadata || analysis.metadata?.fetchMetadata || analysis.fetchMetadata || null;
}

function formatLocalStamp(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString([], { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true });
}

function formatCoverageDuration(start, end) {
  if (!start || !end) return '';
  const hours = (new Date(end) - new Date(start)) / 3600000;
  if (!Number.isFinite(hours) || hours < 0) return '';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))} min`;
  if (hours < 36) return `${hours.toFixed(1)} hours`;
  return `${(hours / 24).toFixed(1)} days`;
}

function formatRequestedRange(hours) {
  const n = Number(hours);
  if (n === 168) return '7 days';
  if (n === 24) return '24 hours';
  if (n === 1) return '1 hour';
  return `${n} hours`;
}

export default function LeakHunterPage() {
  // Global environment context
  const { currentEnv, envName, envColor, isProduction } = useEnvironment();

  // Derive source and environment from global context
  // localhost → source: 'localhost'
  // development/staging/production → source: 'railway', environment: currentEnv
  const source = currentEnv === 'localhost' ? 'localhost' : 'railway';
  const environment = currentEnv === 'localhost' ? undefined : currentEnv;

  // State
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [connectionStatus, setConnectionStatus] = useState(null);

  // Form state (only time range is user-configurable now)
  const [timeRangeHours, setTimeRangeHours] = useState(1);

  // Route Analysis table state
  const [routeSearch, setRouteSearch] = useState('');
  const [routeSort, setRouteSort] = useState({ column: 'count', direction: 'desc' });

  // Tabs configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'pool', label: 'Pool Stats', icon: ServerStackIcon },
    { id: 'routes', label: 'Routes', icon: ListBulletIcon },
    { id: 'history', label: 'History', icon: ClockIcon },
    { id: 'config', label: 'Config', icon: Cog6ToothIcon },
  ];

  // Reset all state when environment changes
  useEffect(() => {
    // Clear all analysis data when switching environments
    setResult(null);
    setSelectedAnalysis(null);
    setHistory([]);
    setConfig(null);
    setConnectionStatus(null);
    setError(null);
    setActiveTab('overview');

    // Fetch fresh config and history for new environment
    fetchConfig();
    fetchHistory();
  }, [currentEnv]);

  const fetchConfig = async () => {
    try {
      const response = await leakhunterAPI.getConfig();
      setConfig(response.data.data);
    } catch (err) {
      console.error('[LeakHunter] Failed to fetch config:', err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await leakhunterAPI.getHistory({ limit: 20 });
      setHistory(response.data.data || []);
    } catch (err) {
      console.error('[LeakHunter] Failed to fetch history:', err);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    setConnectionStatus(null);
    try {
      const response = await leakhunterAPI.testConnection();
      setConnectionStatus(response.data.data);
    } catch (err) {
      setConnectionStatus({ success: false, error: err.message });
    } finally {
      setLoading(false);
    }
  };

  const runAnalysis = async () => {
    setAnalyzing(true);
    setError(null);
    setResult(null);

    try {
      const response = await leakhunterAPI.runAnalysis({
        source,
        environment: source === 'railway' ? environment : undefined,
        timeRangeHours,
      });

      setResult(response.data.data);

      // Fetch the FULL analysis (POST only returns summary, GET returns all data including charts)
      const analysisId = response.data.data.id;
      if (analysisId) {
        const fullAnalysis = await leakhunterAPI.getAnalysis(analysisId);
        const full = fullAnalysis.data.data;
        setSelectedAnalysis({
          ...full,
          metadata: {
            ...(response.data.data.metadata || {}),
            ...(full.metadata || {})
          },
          fetch_metadata: full.fetch_metadata || response.data.data.metadata?.fetchMetadata || null
        });
      } else {
        setSelectedAnalysis(response.data.data);
      }

      // Refresh history
      fetchHistory();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Analysis failed';
      setError(errorMessage);
    } finally {
      setAnalyzing(false);
    }
  };

  const loadAnalysis = async (id) => {
    setLoading(true);
    try {
      const response = await leakhunterAPI.getAnalysis(id);
      setSelectedAnalysis(response.data.data);
      setActiveTab('overview');
    } catch (err) {
      console.error('[LeakHunter] Failed to load analysis:', err);
    } finally {
      setLoading(false);
    }
  };

  // Format milliseconds to readable duration
  const formatDuration = (ms) => {
    if (ms === null || ms === undefined) return 'N/A';
    const num = typeof ms === 'string' ? parseFloat(ms) : ms;
    if (isNaN(num)) return 'N/A';
    if (num < 1000) return `${Math.round(num)}ms`;
    if (num < 60000) return `${(num / 1000).toFixed(1)}s`;
    return `${(num / 60000).toFixed(1)}m`;
  };

  // Format percentage
  const formatPercent = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) return 'N/A';
    return `${num.toFixed(1)}%`;
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <BugAntIcon className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-deep-navy">
            LeakHunter
          </h1>
        </div>
        <p className="text-gray-600">
          Connection pool analysis and leak detection for Railway deployments
        </p>
      </div>

      {/* Control Panel */}
      <div className="card mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <PlayIcon className="w-5 h-5" />
          Run Analysis
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          {/* Current Environment (from global selector) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
            <div
              className="input w-full flex items-center gap-2"
              style={{ borderColor: envColor, backgroundColor: 'white' }}
            >
              <div
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: envColor }}
              />
              <span className="font-medium" style={{ color: envColor }}>
                {envName}
              </span>
              <span className="text-xs text-gray-400 ml-auto">
                {source === 'localhost' ? '(local)' : '(Railway)'}
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Use header selector to change
            </p>
          </div>

          {/* Time Range */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Time Range</label>
            <select
              value={timeRangeHours}
              onChange={(e) => setTimeRangeHours(parseInt(e.target.value))}
              className="input w-full"
              disabled={analyzing}
            >
              {config?.validTimeRanges?.map((hours) => (
                <option key={hours} value={hours}>
                  {hours === 168 ? '7 days' : hours === 24 ? '24 hours' : `${hours} hour${hours > 1 ? 's' : ''}`}
                </option>
              )) || (
                <>
                  <option value={1}>1 hour</option>
                  <option value={6}>6 hours</option>
                  <option value={24}>24 hours</option>
                  <option value={168}>7 days</option>
                </>
              )}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              7-day runs page Railway logs and can take up to about a minute.
            </p>
          </div>

          {/* Spacer for alignment */}
          <div className="hidden md:block" />

          {/* Run Button */}
          <div className="flex items-end">
            <button
              onClick={runAnalysis}
              disabled={analyzing || (source === 'railway' && !config?.railwayConfigured)}
              className="btn-primary w-full flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <PlayIcon className="w-5 h-5" />
                  Run Analysis
                </>
              )}
            </button>
          </div>
        </div>

        {/* Railway Config Warning */}
        {source === 'railway' && config && !config.railwayConfigured && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-start gap-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-800">
              <strong>Railway not configured.</strong> Set RAILWAY_API_TOKEN, RAILWAY_PROJECT_ID, and RAILWAY_SERVICE_ID environment variables.
            </div>
          </div>
        )}

        {/* Test Connection Button */}
        {source === 'railway' && (
          <div className="mt-4 pt-4 border-t border-blue-200">
            <button
              onClick={testConnection}
              disabled={loading}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <ServerStackIcon className="w-4 h-4" />
              {loading ? 'Testing...' : 'Test Railway Connection'}
            </button>
            {connectionStatus && (
              <div className={`mt-2 text-sm ${connectionStatus.success ? 'text-green-600' : 'text-red-600'}`}>
                {connectionStatus.success
                  ? `Connected! Environments: ${connectionStatus.environments?.map((e) => e.name).join(', ')}`
                  : `Error: ${connectionStatus.error}`}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="card mb-6 bg-red-50 border border-red-200">
          <div className="flex items-start gap-3">
            <XCircleIcon className="w-6 h-6 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-900">Analysis Failed</p>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <Tabs tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div>
          {selectedAnalysis ? (
            <>
              {/* Status Banner */}
              <div className={`card mb-6 ${
                selectedAnalysis.status === 'healthy' ? 'bg-green-50 border-green-200' :
                selectedAnalysis.status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
                'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <StatusBadge status={selectedAnalysis.status} />
                    <div>
                      <p className="font-medium text-gray-900">
                        {selectedAnalysis.statusMessage || selectedAnalysis.status_message || 'Analysis complete'}
                      </p>
                      <p className="text-sm text-gray-600">
                        {selectedAnalysis.metadata?.environment || selectedAnalysis.environment} &bull; {' '}
                        {selectedAnalysis.metadata?.timeRangeHours || selectedAnalysis.time_range_hours}h range &bull; {' '}
                        {(selectedAnalysis.metadata?.logsAnalyzed ?? selectedAnalysis.logs_fetched ?? selectedAnalysis.logs_analyzed ?? 0).toLocaleString()} logs analyzed
                        {resolveFetchMeta(selectedAnalysis)?.pagesFetched > 1
                          ? ` across ${resolveFetchMeta(selectedAnalysis).pagesFetched} pages`
                          : ''}
                      </p>
                    </div>
                  </div>
                  <div className="text-right text-sm text-gray-500">
                    <p>ID: {selectedAnalysis.id?.slice(0, 8)}...</p>
                    <p>{new Date(selectedAnalysis.created_at || selectedAnalysis.metadata?.timestamp).toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {(() => {
                const fetchMeta = resolveFetchMeta(selectedAnalysis);
                if (!fetchMeta) return null;
                const sourceName = selectedAnalysis.metadata?.source || selectedAnalysis.source;
                const requestedHours = fetchMeta.requested?.timeRangeHours
                  || selectedAnalysis.metadata?.timeRangeHours
                  || selectedAnalysis.time_range_hours;
                const coverageStart = fetchMeta.connCoverage?.start;
                const coverageEnd = fetchMeta.connCoverage?.end;
                const coverageLabel = coverageStart && coverageEnd
                  ? `${formatLocalStamp(coverageStart)} → ${formatLocalStamp(coverageEnd)} (${formatCoverageDuration(coverageStart, coverageEnd)})`
                  : null;

                if (!fetchMeta.truncated) return null;

                return (
                  <div className="card mb-6 bg-amber-50 border border-amber-200">
                    <div className="flex items-start gap-3">
                      <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-900 space-y-1">
                        {fetchMeta.truncated && (
                          <p>
                            Acquire/release counts cover {coverageLabel || 'a partial window'}, not the full {formatRequestedRange(requestedHours)}.
                            {sourceName === 'localhost'
                              ? ' The local buffer only keeps recent connection events.'
                              : ` Railway returns 5,000 connection logs per page; this run stopped after ${fetchMeta.pagesFetched || 1} page${(fetchMeta.pagesFetched || 1) === 1 ? '' : 's'}.`}
                          </p>
                        )}
                        {sourceName !== 'localhost' && (
                          <p>Leak and long-held scan covers the full requested range.</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* Summary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <MetricCard
                  label="Total Acquires"
                  value={selectedAnalysis.summary?.totalAcquires ?? selectedAnalysis.total_acquires ?? 0}
                  icon={ServerStackIcon}
                  color="blue"
                />
                <MetricCard
                  label="Total Releases"
                  value={selectedAnalysis.summary?.totalReleases ?? selectedAnalysis.total_releases ?? 0}
                  icon={ServerStackIcon}
                  color="green"
                />
                <MetricCard
                  label="Waiting"
                  value={selectedAnalysis.summary?.waiting ?? selectedAnalysis.waiting ?? 0}
                  subValue="Requests queued for connection"
                  icon={ChartBarIcon}
                  color={(selectedAnalysis.summary?.waiting ?? selectedAnalysis.waiting ?? 0) > 0 ? 'red' : 'green'}
                />
                <MetricCard
                  label="Leaks Detected"
                  value={selectedAnalysis.summary?.leakCount ?? selectedAnalysis.leak_count ?? 0}
                  subValue="Connections held > 30 sec"
                  icon={BugAntIcon}
                  color={(selectedAnalysis.summary?.leakCount ?? selectedAnalysis.leak_count ?? 0) > 0 ? 'red' : 'green'}
                />
              </div>

              {/* Recommendations */}
              {(selectedAnalysis.recommendations?.length > 0 || selectedAnalysis.sample_events) && (
                <div className="card mb-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Recommendations</h3>
                  <ul className="space-y-2">
                    {(selectedAnalysis.recommendations || []).map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 mt-0.5 flex-shrink-0" />
                        <span>{rec}</span>
                      </li>
                    ))}
                    {(!selectedAnalysis.recommendations || selectedAnalysis.recommendations.length === 0) && (
                      <li className="flex items-start gap-2 text-sm text-gray-500">
                        <CheckCircleIcon className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>No issues detected. Pool is healthy.</span>
                      </li>
                    )}
                  </ul>
                </div>
              )}

              {/* Full Analysis Data */}
              <JsonViewer data={selectedAnalysis} title="Full Analysis Data" />
            </>
          ) : (
            <div className="card text-center py-12 text-gray-500">
              <BugAntIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No Analysis Selected</p>
              <p className="text-sm mt-1">Run a new analysis or select one from history</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'pool' && selectedAnalysis && (
        <div className="space-y-6">
          {/* Metric Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <MetricCard
              label="Peak Utilization"
              value={formatPercent(selectedAnalysis.summary?.peakUtilization ?? selectedAnalysis.peak_utilization_percent)}
              icon={ChartBarIcon}
              color="purple"
            />
            <MetricCard
              label="Avg Utilization"
              value={formatPercent(selectedAnalysis.avg_utilization_percent)}
              icon={ChartBarIcon}
              color="blue"
            />
            <MetricCard
              label="Avg Hold Time"
              value={formatDuration(selectedAnalysis.summary?.avgHoldTime ?? selectedAnalysis.avg_held_time_ms ?? 0)}
              icon={ClockIcon}
              color="blue"
            />
            <MetricCard
              label="P95 Hold Time"
              value={formatDuration(selectedAnalysis.p95_held_time_ms ?? 0)}
              icon={ClockIcon}
              color="yellow"
            />
          </div>

          <PoolStatsPanel analysis={selectedAnalysis} />
        </div>
      )}

      {activeTab === 'routes' && selectedAnalysis && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Route Analysis</h3>
            {/* Search bar */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search routes..."
                value={routeSearch}
                onChange={(e) => setRouteSearch(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-64"
              />
            </div>
          </div>
          {selectedAnalysis.route_analysis && (Array.isArray(selectedAnalysis.route_analysis) ? selectedAnalysis.route_analysis.length > 0 : Object.keys(selectedAnalysis.route_analysis).length > 0) ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <SortableHeader label="Route" column="route" sortState={routeSort} onSort={setRouteSort} />
                    <SortableHeader label="Acquires" column="count" sortState={routeSort} onSort={setRouteSort} align="right" />
                    <SortableHeader label="Leaks" column="leaks" sortState={routeSort} onSort={setRouteSort} align="right" />
                    <SortableHeader label="Avg Hold" column="avgHoldTime" sortState={routeSort} onSort={setRouteSort} align="right" />
                    <SortableHeader label="P95 Hold" column="p95HoldTime" sortState={routeSort} onSort={setRouteSort} align="right" />
                    <SortableHeader label="Status" column="status" sortState={routeSort} onSort={setRouteSort} align="center" />
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Normalize route data
                    let routes = Array.isArray(selectedAnalysis.route_analysis)
                      ? selectedAnalysis.route_analysis
                      : Object.entries(selectedAnalysis.route_analysis).map(([route, data]) => ({ route, ...data }));

                    // Filter by search term
                    if (routeSearch) {
                      const searchLower = routeSearch.toLowerCase();
                      routes = routes.filter((r) => r.route?.toLowerCase().includes(searchLower));
                    }

                    // Sort by selected column
                    const statusOrder = { critical: 0, warning: 1, healthy: 2 };
                    routes = [...routes].sort((a, b) => {
                      let aVal, bVal;
                      switch (routeSort.column) {
                        case 'route':
                          aVal = a.route || '';
                          bVal = b.route || '';
                          break;
                        case 'count':
                          aVal = a.count || a.acquireCount || 0;
                          bVal = b.count || b.acquireCount || 0;
                          break;
                        case 'leaks':
                          aVal = a.leaks || a.leakCount || 0;
                          bVal = b.leaks || b.leakCount || 0;
                          break;
                        case 'avgHoldTime':
                          aVal = a.avgHoldTime || 0;
                          bVal = b.avgHoldTime || 0;
                          break;
                        case 'p95HoldTime':
                          aVal = a.p95HoldTime || 0;
                          bVal = b.p95HoldTime || 0;
                          break;
                        case 'status':
                          aVal = statusOrder[a.status || 'healthy'] ?? 2;
                          bVal = statusOrder[b.status || 'healthy'] ?? 2;
                          break;
                        default:
                          aVal = 0;
                          bVal = 0;
                      }
                      if (typeof aVal === 'string') {
                        const cmp = aVal.localeCompare(bVal);
                        return routeSort.direction === 'asc' ? cmp : -cmp;
                      }
                      return routeSort.direction === 'asc' ? aVal - bVal : bVal - aVal;
                    });

                    return routes.map((routeData) => (
                      <tr key={routeData.route} className="border-b hover:bg-gray-50">
                        <td className="py-2 px-3 font-mono text-xs">
                          {routeData.route}
                          {routeData.isSlowRoute && (
                            <span className="ml-2 text-xs text-gray-400">(slow)</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{routeData.count || routeData.acquireCount || 0}</td>
                        <td className="py-2 px-3 text-right">
                          {(routeData.leaks || routeData.leakCount || 0) > 0 ? (
                            <span className="text-red-600 font-medium">{routeData.leaks || routeData.leakCount}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="py-2 px-3 text-right">{formatDuration(routeData.avgHoldTime || 0)}</td>
                        <td className="py-2 px-3 text-right">{formatDuration(routeData.p95HoldTime || 0)}</td>
                        <td className="py-2 px-3 text-center">
                          <StatusBadge status={routeData.status || 'healthy'} />
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No route data available</p>
          )}
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-800">Analysis History</h3>
            <button
              onClick={fetchHistory}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <ArrowPathIcon className="w-4 h-4" />
              Refresh
            </button>
          </div>
          {history.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-3">Date</th>
                    <th className="text-left py-2 px-3">Environment</th>
                    <th className="text-left py-2 px-3">Status</th>
                    <th className="text-right py-2 px-3">Logs</th>
                    <th className="text-right py-2 px-3">Leaks</th>
                    <th className="text-right py-2 px-3">Duration</th>
                    <th className="text-center py-2 px-3">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((analysis) => (
                    <tr
                      key={analysis.id}
                      className={`border-b hover:bg-gray-50 cursor-pointer ${
                        selectedAnalysis?.id === analysis.id ? 'bg-blue-50' : ''
                      }`}
                      onClick={() => loadAnalysis(analysis.id)}
                    >
                      <td className="py-2 px-3">{new Date(analysis.analyzed_at).toLocaleString()}</td>
                      <td className="py-2 px-3">{analysis.environment}</td>
                      <td className="py-2 px-3">
                        <StatusBadge status={analysis.status} />
                      </td>
                      <td className="py-2 px-3 text-right">{analysis.logs_fetched || 0}</td>
                      <td className="py-2 px-3 text-right">
                        {analysis.leak_count > 0 ? (
                          <span className="text-red-600 font-medium">{analysis.leak_count}</span>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">{formatDuration(analysis.duration_ms || 0)}</td>
                      <td className="py-2 px-3 text-center">
                        <button className="text-blue-600 hover:text-blue-800 text-xs">
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No analyses yet. Run your first analysis above.</p>
          )}
        </div>
      )}

      {activeTab === 'config' && (
        <div className="card">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuration & Thresholds</h3>
          {config ? (
            <div className="space-y-6">
              <div>
                <h4 className="font-medium text-gray-700 mb-2">Valid Options</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-gray-500">Sources</p>
                    <p className="font-mono">{config.validSources?.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Environments</p>
                    <p className="font-mono">{config.validEnvironments?.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-gray-500">Time Ranges</p>
                    <p className="font-mono">{config.validTimeRanges?.map((h) => `${h}h`).join(', ')}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-medium text-gray-700 mb-2">Thresholds</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    {config.thresholds && Object.entries(config.thresholds).map(([key, value]) => (
                      <div key={key}>
                        <p className="text-gray-500">{key}</p>
                        <p className="font-mono font-medium">
                          {typeof value === 'number' && key.includes('TIME') ? `${value}ms` :
                           typeof value === 'number' && key.includes('UTILIZATION') ? `${value}%` :
                           value}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Railway Status</h4>
                  <p className={config.railwayConfigured ? 'text-green-600' : 'text-red-600'}>
                    {config.railwayConfigured ? 'Configured' : 'Not configured'}
                  </p>
                  {config.railwayPagination && (
                    <p className="text-xs text-gray-500 mt-2 font-mono">
                      page {config.railwayPagination.pageSize} · max {config.railwayPagination.maxPages} pages · {config.railwayPagination.maxFetchMs}ms
                    </p>
                  )}
                </div>

                <div>
                  <h4 className="font-medium text-gray-700 mb-2">Localhost Buffer</h4>
                  {config.localhostBuffer ? (
                    <div className="text-sm">
                      <p className="text-gray-600">
                        {config.localhostBuffer.count?.toLocaleString() || 0} / {config.localhostBuffer.capacity?.toLocaleString() || 10000} events
                      </p>
                      <p className="text-gray-500">
                        {config.localhostBuffer.utilizationPercent || 0}% utilized
                      </p>
                      {config.localhostBuffer.oldestEvent && (
                        <p className="text-xs text-gray-400 mt-1">
                          Oldest: {new Date(config.localhostBuffer.oldestEvent).toLocaleTimeString()}
                        </p>
                      )}
                    </div>
                  ) : (
                    <p className="text-gray-400 text-sm">No data</p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-gray-500">Loading configuration...</p>
          )}
        </div>
      )}

      {/* API Reference */}
      <div className="card bg-blue-50 border border-blue-200 mt-6">
        <h3 className="font-semibold text-blue-900 mb-3">API Endpoints</h3>
        <div className="space-y-2 text-sm font-mono text-blue-800">
          <div>
            <span className="text-green-600 font-semibold">POST</span>{' '}
            /api/admin/leakhunter/analyze
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/leakhunter/analysis/:id
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/leakhunter/history
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/leakhunter/test-connection
          </div>
          <div>
            <span className="text-blue-600 font-semibold">GET</span>{' '}
            /api/admin/leakhunter/config
          </div>
        </div>
      </div>
    </div>
  );
}
